// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import {
  Button,
  ChevronDownIcon,
  Flex,
  Link,
  Popover,
  Typography,
} from '@iheartjane/reefer';
import { spacing } from '@iheartjane/reefer-emotion';
import { Form, useForm } from '@iheartjane/reefer-hook-form';
import type { ReactNode } from 'react';
import { useContext, useMemo } from 'react';

import { BoostMenusTableContext } from '@jane/business-admin/providers';

const FiltersWrapper = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  ...spacing({ pt: 40, px: 64 }),
});

const StyledSearchField = styled(Form.SearchField)({
  width: '300px',
});

interface BoostMenusFiltersProps {
  availableIntegrationTypes: Array<{ count: number; type: string }>;
  filteredBoostMenus: Array<{ id: number; integration_method: string }>;
  filteredCount: number;
  integrationMethodFilter: string;
  onOpenBulkImportModal?: () => void;
  searchFilter: string;
  setIntegrationMethodFilter: (value: string) => void;
  setSearchFilter: (value: string) => void;
  totalCount: number;
}

export const BoostMenusFilters = ({
  searchFilter,
  setSearchFilter,
  integrationMethodFilter,
  setIntegrationMethodFilter,
  totalCount,
  filteredCount,
  availableIntegrationTypes,
  filteredBoostMenus,
  onOpenBulkImportModal,
}: BoostMenusFiltersProps) => {
  const {
    bulkEditModalOpen,
    setBulkEditModalOpen,
    bulkSelectedBoostMenus,
    setRowSelection,
  } = useContext(BoostMenusTableContext);

  const formMethods = useForm({
    defaultValues: {
      search: searchFilter,
    },
  });

  const integrationMethodOptions = useMemo(() => {
    const baseOptions: Array<{ count?: number; label: string; value: string }> =
      [{ label: 'All Types', value: 'all' }];

    // Map of integration method values to display labels
    const labelMap: Record<string, string> = {
      wordpress: 'WordPress',
      subdomain: 'Subdomain',
      reverse_proxy: 'Reverse Proxy',
      static: 'Static',
    };

    // Add options for available integration types with counts
    const typeOptions = availableIntegrationTypes.map(({ type, count }) => ({
      label: labelMap[type] || type.charAt(0).toUpperCase() + type.slice(1),
      value: type,
      count,
    }));

    return [...baseOptions, ...typeOptions];
  }, [availableIntegrationTypes]);

  const selectedIntegrationMethod = useMemo(() => {
    const option = integrationMethodOptions.find(
      (opt) => opt.value === integrationMethodFilter
    );
    if (!option || option.value === 'all') {
      return 'All Types';
    }

    const displayLabel = option.label;
    const count = 'count' in option ? option.count : undefined;

    if (count !== undefined) {
      return `${displayLabel} (${count})`;
    }

    return displayLabel;
  }, [integrationMethodFilter, integrationMethodOptions]);

  const hasActiveFilters = useMemo(() => {
    return (
      searchFilter ||
      (integrationMethodFilter && integrationMethodFilter !== 'all')
    );
  }, [searchFilter, integrationMethodFilter]);

  const clearFilters = () => {
    setSearchFilter('');
    setIntegrationMethodFilter('all');
    formMethods.setValue('search', '');
  };

  return (
    <Form name="boost-menus-filters" onSubmit={() => null}>
      <FiltersWrapper>
        <Flex gap={16} alignItems="center">
          <StyledSearchField
            name="search"
            label="Search"
            placeholder="Search store names or URLs"
            autoFocus
            isDebounced={false}
            value={searchFilter}
            onChange={(value) => {
              setSearchFilter(value as string);
              formMethods.setValue('search', value as string);
            }}
          />

          <Popover
            target={
              <Flex>
                <Button
                  full
                  variant="tertiary"
                  label={selectedIntegrationMethod}
                  endIcon={<ChevronDownIcon size="sm" />}
                />
              </Flex>
            }
            mx={16}
            label=""
          >
            {({ closePopover }) => (
              <Popover.Content label="Integration type filter" padding={false}>
                <Form.RadioFieldGroup
                  name="integration-type-list"
                  defaultChecked={integrationMethodFilter || 'all'}
                  onChange={(value: string) => {
                    setIntegrationMethodFilter(value);
                    closePopover();
                  }}
                  options={integrationMethodOptions.map((option, index) => ({
                    id: option.value,
                    label:
                      option.value === 'all' ? (
                        option.label
                      ) : (
                        <Flex alignItems="center" gap={4}>
                          {option.label}
                          {'count' in option && option.count !== undefined && (
                            <Typography
                              variant="body"
                              color="grays-mid"
                              style={{ fontSize: '0.875rem' }}
                            >
                              ({option.count})
                            </Typography>
                          )}
                        </Flex>
                      ),
                    value: option.value,
                    wrapper: (children: ReactNode) => (
                      <Flex
                        mx={16}
                        mt={16}
                        mb={
                          index === integrationMethodOptions.length - 1 ? 16 : 0
                        }
                        ariaLabel={`${option.value}-label`}
                        onClick={() => {
                          setIntegrationMethodFilter(option.value);
                          closePopover();
                        }}
                      >
                        {children}
                      </Flex>
                    ),
                  }))}
                />
              </Popover.Content>
            )}
          </Popover>

          {hasActiveFilters && (
            <Link onClick={clearFilters}>Clear filters</Link>
          )}

          {!bulkEditModalOpen && (
            <Typography variant="body" color="grays-mid">
              {filteredCount} of {totalCount} boost menus
            </Typography>
          )}
        </Flex>

        <Flex gap={16} alignItems="center" justifyContent="flex-end">
          {bulkEditModalOpen ? (
            <>
              <Link
                onClick={() => {
                  // Select all importable menus in the current filtered view
                  const importableMenuIds = filteredBoostMenus
                    .filter((menu) =>
                      ['subdomain', 'reverse_proxy'].includes(
                        menu.integration_method
                      )
                    )
                    .reduce((acc: Record<string, boolean>, menu) => {
                      acc[menu.id.toString()] = true;
                      return acc;
                    }, {});
                  setRowSelection(importableMenuIds);
                }}
              >
                Select all
              </Link>
              <Link onClick={() => setRowSelection({})}>Select none</Link>
              <Typography variant="body" color="grays-mid">
                {bulkSelectedBoostMenus.length} selected
              </Typography>
              {bulkSelectedBoostMenus.length > 0 && (
                <Button
                  variant="primary"
                  label="Import Templates"
                  onClick={onOpenBulkImportModal}
                />
              )}
              <Button
                variant="secondary"
                label="Cancel"
                onClick={() => {
                  setBulkEditModalOpen(false);
                  setRowSelection({});
                }}
              />
            </>
          ) : (
            <Button
              variant="secondary"
              label="Bulk action"
              onClick={() => setBulkEditModalOpen(true)}
            />
          )}
        </Flex>
      </FiltersWrapper>
    </Form>
  );
};
